import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, RotateCcw, Send, Bot } from 'lucide-react';
import { useFlowBuilderStore, nodeTypeConfig } from '../../stores/flowBuilderStore';
import { getOutputs } from './CustomNode';
import type { NodeType } from '../../types';

type Msg = { id: number; who: 'bot' | 'user' | 'sys'; text: string };
type Quick = { label: string; handle: string | null | undefined; nodeId: string };
type Awaiting = { nodeId: string; kind: 'input' | 'choice' | 'branch' } | null;

interface TestDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Start the walkthrough from this node instead of START ("Testar daqui"). */
  startNodeId?: string | null;
}

const MAX_STEPS = 200;
const BRANCH_TYPES = new Set<string>([
  'CONDITION',
  'MESSAGE_TYPE',
  'SEARCH_CONTACT',
  'EXTRACT_DATA',
  'BUSINESS_HOURS',
  'SWITCH',
]);

/**
 * Local walkthrough of the flow — no backend, no AI/CRM calls.
 * Messages become bubbles, buttons/choices become quick replies and
 * branch nodes let the tester pick the path by hand.
 */
export function TestDrawer({ open, onClose, startNodeId }: TestDrawerProps) {
  const nodes = useFlowBuilderStore((state) => state.nodes);
  const edges = useFlowBuilderStore((state) => state.edges);

  const [log, setLog] = useState<Msg[]>([]);
  const [quick, setQuick] = useState<Quick[]>([]);
  const [awaiting, setAwaiting] = useState<Awaiting>(null);
  const [input, setInput] = useState('');
  const [ended, setEnded] = useState(false);
  const seq = useRef(0);
  const logRef = useRef<HTMLDivElement>(null);

  const byId = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);

  const startId = useMemo(
    () => startNodeId ?? nodes.find((node) => (node.data as any)?.type === 'START')?.id ?? null,
    [startNodeId, nodes],
  );

  const nextFrom = useCallback(
    (nodeId: string, handle?: string | null): string | null => {
      const fromNode = edges.filter((edge) => edge.source === nodeId);
      if (handle !== undefined) {
        const exact = fromNode.find((edge) => (edge.sourceHandle ?? null) === handle);
        return exact?.target ?? null;
      }
      const single = fromNode.find((edge) => edge.sourceHandle == null) ?? fromNode[0];
      return single?.target ?? null;
    },
    [edges],
  );

  const push = (who: Msg['who'], text: string) => {
    setLog((prev) => [...prev, { id: ++seq.current, who, text }]);
  };

  const run = useCallback(
    (fromId: string | null) => {
      const batch: Msg[] = [];
      const add = (who: Msg['who'], text: string) => batch.push({ id: ++seq.current, who, text });
      let cur: string | null = fromId;
      let steps = 0;
      let newQuick: Quick[] = [];
      let newAwaiting: Awaiting = null;
      let done = false;

      while (cur && steps++ < MAX_STEPS) {
        const node = byId.get(cur);
        if (!node) {
          add('sys', 'Passo não encontrado — fim');
          done = true;
          break;
        }
        const data = node.data as any;
        const type = String(data?.type || '') as NodeType;
        const cfg = (data?.config || {}) as Record<string, any>;
        const typeLabel = nodeTypeConfig[type]?.label || type;
        const label = String(data?.label || typeLabel);
        const here: string = cur;

        switch (type) {
          case 'START':
            cur = nextFrom(here);
            break;
          case 'SEND_MESSAGE':
            if (cfg.message) add('bot', String(cfg.message));
            cur = nextFrom(here);
            break;
          case 'WHISPER_MESSAGE':
            if (cfg.message) add('bot', `🤫 (só o operador vê) ${cfg.message}`);
            cur = nextFrom(here);
            break;
          case 'SEND_FILE':
            add('bot', `📎 ${cfg.fileName || 'Arquivo'}${cfg.caption ? ` — ${cfg.caption}` : ''}`);
            cur = nextFrom(here);
            break;
          case 'SEND_BUTTONS': {
            if (cfg.message) add('bot', String(cfg.message));
            const buttons = Array.isArray(cfg.buttons) ? cfg.buttons : [];
            newQuick = buttons.map((btn: any, i: number) => ({
              label: String(btn?.text || `Botão ${i + 1}`),
              handle: `button-${i}`,
              nodeId: here,
            }));
            if (cfg.fallbackEnabled) newQuick.push({ label: 'Outro…', handle: 'fallback', nodeId: here });
            if (newQuick.length === 0) {
              add('sys', 'Sem botões configurados — fim');
              done = true;
            } else {
              newAwaiting = { nodeId: here, kind: 'choice' };
            }
            cur = null;
            break;
          }
          case 'WAIT_CHOICE': {
            const choices = Array.isArray(cfg.validChoices) ? cfg.validChoices : [];
            newQuick = choices.map((choice: string, i: number) => ({
              label: String(choice || `Opção ${i + 1}`),
              handle: `choice-${i}`,
              nodeId: here,
            }));
            if (newQuick.length === 0) {
              add('sys', 'Sem opções configuradas — fim');
              done = true;
            } else {
              newAwaiting = { nodeId: here, kind: 'choice' };
            }
            cur = null;
            break;
          }
          case 'WAIT_INPUT':
            if (cfg.prompt) add('bot', String(cfg.prompt));
            add('sys', `Aguardando sua resposta${cfg.variableName ? ` → {{${cfg.variableName}}}` : ''}`);
            newAwaiting = { nodeId: here, kind: 'input' };
            cur = null;
            break;
          case 'AI_RESPONSE':
            add(
              'sys',
              `🤖 ${label} — a IA responderia aqui (${cfg.provider || 'IA'} · ${cfg.model || 'modelo'}). A simulação não chama a IA.`,
            );
            cur = nextFrom(here);
            break;
          case 'END_SESSION':
            if (cfg.message) add('bot', String(cfg.message));
            add('sys', '■ Fim da sessão');
            done = true;
            cur = null;
            break;
          case 'TRANSFER_TO_OPERATOR':
          case 'TRANSFER_TO_QUEUE':
            if (cfg.message) add('bot', String(cfg.message));
            add('sys', `→ ${typeLabel}: conversa entregue ao humano`);
            done = true;
            cur = null;
            break;
          default:
            if (BRANCH_TYPES.has(type)) {
              const outs = getOutputs(data);
              newQuick = outs.map((out) => ({ label: out.label, handle: out.id ?? null, nodeId: here }));
              add('sys', `⚙ ${typeLabel} · ${label} — escolha o caminho:`);
              newAwaiting = { nodeId: here, kind: 'branch' };
              cur = null;
            } else {
              add('sys', `⚙ ${typeLabel} · ${label}`);
              cur = nextFrom(here);
            }
        }

        if (cur === null && !newAwaiting && !done) {
          add('sys', '■ Fim do fluxo (sem próximo passo)');
          done = true;
        }
      }

      if (steps >= MAX_STEPS) {
        add('sys', '⚠ Limite de passos atingido (possível loop)');
        done = true;
      }

      setLog((prev) => [...prev, ...batch]);
      setQuick(newQuick);
      setAwaiting(newAwaiting);
      if (done) setEnded(true);
    },
    [byId, nextFrom],
  );

  const restart = useCallback(() => {
    setLog([]);
    setQuick([]);
    setAwaiting(null);
    setEnded(false);
    setInput('');
    seq.current = 0;
    if (!startId) {
      setLog([{ id: 1, who: 'sys', text: 'Nenhum passo de Início encontrado' }]);
      setEnded(true);
      return;
    }
    run(startId);
  }, [startId, run]);

  // Reset only when the drawer opens or the start node changes — never mid-test on graph edits.
  useEffect(() => {
    if (open) restart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startNodeId]);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log, quick]);

  const follow = (nodeId: string, handle: string | null | undefined, fallbackLabel: string) => {
    const target = nextFrom(nodeId, handle);
    setQuick([]);
    setAwaiting(null);
    if (!target) {
      push('sys', `Sem conexão em "${fallbackLabel}" — fim`);
      setEnded(true);
      return;
    }
    run(target);
  };

  const pick = (q: Quick) => {
    push('user', q.label);
    follow(q.nodeId, q.handle, q.label);
  };

  const send = () => {
    const text = input.trim();
    if (!text || !awaiting || awaiting.kind === 'branch') return;
    push('user', text);
    setInput('');
    if (awaiting.kind === 'input') {
      const target = nextFrom(awaiting.nodeId);
      setAwaiting(null);
      if (!target) {
        push('sys', '■ Fim do fluxo');
        setEnded(true);
        return;
      }
      run(target);
      return;
    }
    // choice: accept typed text that matches an option
    const match = quick.find((q) => q.label.trim().toLowerCase() === text.toLowerCase());
    if (match) {
      follow(match.nodeId, match.handle, match.label);
    } else {
      push('sys', 'Não entendi — escolha uma das opções acima.');
    }
  };

  if (!open) return null;

  const canType = !!awaiting && awaiting.kind !== 'branch';
  const placeholder = !awaiting
    ? ended
      ? 'Simulação encerrada'
      : 'Aguarde…'
    : awaiting.kind === 'input'
    ? 'Digite sua resposta…'
    : awaiting.kind === 'choice'
    ? 'Escolha acima ou digite a opção…'
    : 'Escolha um caminho acima';

  return (
    <aside className="fixed right-0 top-0 z-[55] flex h-screen w-[min(380px,100vw)] flex-col border-l border-slate-700/70 bg-slate-900 shadow-[-20px_0_60px_rgba(0,0,0,0.55)]">
      <header className="flex h-[52px] items-center gap-2 border-b border-slate-700/70 px-4">
        <span className="grid h-7 w-7 place-items-center rounded-md bg-violet-500/20 text-violet-300">
          <Bot className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-medium leading-tight text-slate-100">Testar fluxo</p>
          <p className="text-[10.5px] leading-tight text-slate-400">Simulação local — não chama IA/CRM</p>
        </div>
        <button
          type="button"
          onClick={restart}
          title="Reiniciar"
          className="grid h-8 w-8 place-items-center rounded-lg text-slate-300 hover:bg-slate-800"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onClose}
          title="Fechar"
          className="grid h-8 w-8 place-items-center rounded-lg text-slate-300 hover:bg-slate-800"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div ref={logRef} className="flex-1 space-y-2.5 overflow-y-auto px-4 py-4">
        {log.map((m) =>
          m.who === 'sys' ? (
            <p key={m.id} className="fb-msg-in px-6 text-center text-[10.5px] leading-snug text-slate-500">
              {m.text}
            </p>
          ) : (
            <div key={m.id} className={`fb-msg-in flex ${m.who === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[82%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-[13px] leading-snug ${
                  m.who === 'user'
                    ? 'rounded-br-md bg-violet-600 text-white'
                    : 'rounded-bl-md bg-slate-800 text-slate-100'
                }`}
              >
                {m.text}
              </div>
            </div>
          ),
        )}

        {quick.length > 0 && (
          <div className="fb-msg-in flex flex-wrap gap-1.5 pt-1">
            {quick.map((q, i) => (
              <button
                key={`${q.handle ?? 'out'}-${i}`}
                type="button"
                onClick={() => pick(q)}
                className="rounded-full border border-slate-600 px-3 py-1.5 text-[12.5px] text-slate-200 transition-colors hover:border-violet-500 hover:bg-violet-500/10"
              >
                {q.label}
              </button>
            ))}
          </div>
        )}

        {ended && quick.length === 0 && (
          <div className="pt-2 text-center">
            <button
              type="button"
              onClick={restart}
              className="text-[12px] text-violet-300 underline-offset-2 hover:text-violet-200 hover:underline"
            >
              Reiniciar simulação
            </button>
          </div>
        )}
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          send();
        }}
        className="flex items-center gap-2 border-t border-slate-700/70 p-3"
      >
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          disabled={!canType}
          placeholder={placeholder}
          className="h-9 flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 text-[13px] text-slate-100 placeholder:text-slate-500 focus:border-violet-500 focus:outline-none disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!canType || !input.trim()}
          className="grid h-9 w-9 place-items-center rounded-lg bg-violet-600 text-white transition-colors hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-40"
          title="Enviar"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </aside>
  );
}
